/**
 * Redux Slice Error Handling Utilities
 *
 * Provides standardized error handling for Redux async thunks
 * with consistent error message formatting across all slices.
 */

/**
 * API Error interface
 */
export interface ApiError {
  message: string
  status?: number
  code?: string
}

/**
 * Standardized error messages for common operations
 */
export const ERROR_MESSAGES = {
  FETCH: '데이터 조회에 실패했습니다.',
  CREATE: '생성에 실패했습니다.',
  UPDATE: '수정에 실패했습니다.',
  DELETE: '삭제에 실패했습니다.',
  NETWORK: '네트워크 연결을 확인해주세요.',
  PERMISSION: '권한이 없습니다.',
  VALIDATION: '입력 데이터를 확인해주세요.',
  SERVER: '서버 오류가 발생했습니다.',
  UNKNOWN: '알 수 없는 오류가 발생했습니다.'
} as const

/**
 * Type for error message keys
 */
export type ErrorMessageKey = keyof typeof ERROR_MESSAGES

/**
 * Formats error object into user-friendly Korean message
 *
 * Handles various error types:
 * - API errors with status codes (400, 401, 403, 404, 500, etc.)
 * - Errors with message property
 * - String errors
 * - Unknown errors
 *
 * @param error - Error object from API or other source
 * @param defaultMessage - Fallback message if error cannot be parsed
 * @returns Formatted error message string
 */
const formatErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (error && typeof error === 'object') {
    // API error with HTTP status code
    if ('status' in error) {
      const status = (error as ApiError).status
      switch (status) {
        case 400:
          return '잘못된 요청입니다.'
        case 401:
          return '인증이 필요합니다.'
        case 403:
          return '권한이 없습니다.'
        case 404:
          return '요청한 리소스를 찾을 수 없습니다.'
        case 409:
          return '충돌이 발생했습니다.'
        case 422:
          return '입력 데이터가 올바르지 않습니다.'
        case 500:
          return '서버 오류가 발생했습니다.'
        case 502:
          return '서버에 연결할 수 없습니다.'
        case 503:
          return '서비스를 사용할 수 없습니다.'
        default:
          return (error as ApiError).message || defaultMessage
      }
    }

    // Error with message property
    if ('message' in error) {
      return (error as ApiError).message
    }
  }

  // String error
  if (typeof error === 'string') {
    return error
  }

  // Unknown error format
  return defaultMessage
}

/**
 * Standard error handler for async thunks
 *
 * Use this in the catch block of createAsyncThunk to ensure
 * consistent error handling across all Redux slices.
 *
 * @param error - Error caught in async thunk
 * @param rejectWithValue - Redux Toolkit's rejectWithValue function
 * @param defaultMessage - Default message to show if error cannot be parsed
 * @returns Rejected value with formatted error message
 *
 * @example
 * ```typescript
 * export const fetchData = createAsyncThunk(
 *   'slice/fetchData',
 *   async (params, { rejectWithValue }) => {
 *     try {
 *       const response = await api.getData(params)
 *       return response
 *     } catch (error: unknown) {
 *       return handleAsyncThunkError(error, rejectWithValue, '데이터 조회에 실패했습니다.')
 *     }
 *   }
 * )
 * ```
 */
export const handleAsyncThunkError = (
  error: unknown,
  rejectWithValue: (value: string) => any,
  defaultMessage: string
): ReturnType<typeof rejectWithValue> => {
  const errorMessage = formatErrorMessage(error, defaultMessage)
  return rejectWithValue(errorMessage)
}